import React, { useCallback, useEffect, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { Col, Form, Row, Spin } from 'antd'
import PropTypes from 'prop-types'

import {
  DirtyPromptForm,
  FormButtons,
  FormCheckbox,
  FormSelect,
  FormTextareaInput,
} from '../../../common/components/sections'
import {
  StyledPageHeader,
  StyledPageTitle,
} from '../../../common/components/styled'
import { PERMISSIONS } from '../../../common/constants'
import {
  createSelectOptions,
  showErrorNotification,
} from '../../../common/helpers'
import { useFormDirty, useUserHasPermissions } from '../../../common/hooks'
import { idPropType } from '../../../common/prop-types'
import { generateArrayOfNumbers } from '../../../common/utils'

import { useUpdateDishTypeSimpleDetailsMutation } from '../../hooks'
import { setDishTypePageIsDirty } from '../../redux/actions'

import { dishTypeSimpleDetailsFormInitialValues } from './initial-values'

const DishTypeSimpleDetailsForm = ({
  id,
  name,
  description,
  synonyms,
  position,
  isExcludedFromSearch,
  isExcludedFromBrowsing,
  maxPosition,
}) => {
  const dispatch = useDispatch()
  const [form] = Form.useForm()

  const isFormEnabled = useUserHasPermissions([
    PERMISSIONS.DISH_TYPE.UPDATE_SIMPLE_DETAILS,
  ])

  const [
    updateDishTypeSimpleDetails,
    { isLoading: isSaveLoading, error: saveError },
  ] = useUpdateDishTypeSimpleDetailsMutation()

  const { isDirty, setToDirty, resetForm } = useFormDirty(form)

  const initialValues = useMemo(
    () =>
      dishTypeSimpleDetailsFormInitialValues({
        description,
        synonyms,
        position,
        isExcludedFromSearch,
        isExcludedFromBrowsing,
      }),
    [
      description,
      isExcludedFromSearch,
      isExcludedFromBrowsing,
      position,
      synonyms,
    ],
  )

  const positionOptions = useMemo(
    () => createSelectOptions(generateArrayOfNumbers(maxPosition)),
    [maxPosition],
  )

  const handleSaveData = useCallback(
    data => updateDishTypeSimpleDetails(id, data),
    [id, updateDishTypeSimpleDetails],
  )

  useEffect(() => {
    if (saveError) {
      showErrorNotification({
        title: 'Dish Type details error',
        description: saveError,
      })
    }
  }, [saveError])

  useEffect(() => {
    resetForm()
  }, [description, synonyms, position, resetForm])

  useEffect(() => {
    dispatch(setDishTypePageIsDirty({ isSimpleDetailsDirty: isDirty }))
  }, [dispatch, isDirty])

  return isSaveLoading ? (
    <Spin />
  ) : (
    <DirtyPromptForm
      isDirty={isDirty}
      form={form}
      initialValues={initialValues}
      name="editDishType"
      onFinish={handleSaveData}
      autoComplete="off"
      onFieldsChange={setToDirty}
    >
      <StyledPageHeader>
        <StyledPageTitle>{name}</StyledPageTitle>
      </StyledPageHeader>

      <Row gutter={[16]}>
        <Col span={12}>
          <FormTextareaInput
            disabled={!isFormEnabled}
            name="description"
            label="Description"
            labelAbove
            placeholder="Input description..."
            autoSize
          />
        </Col>
        <Col span={12}>
          <FormTextareaInput
            disabled={!isFormEnabled}
            name="synonyms"
            label="Synonyms (separated by new line)"
            labelAbove
            placeholder="Input synonyms..."
            autoSize
          />
        </Col>
        <Col span={12}>
          <FormSelect
            disabled={!isFormEnabled}
            name="position"
            labelAbove
            label="Position"
            showSearch={false}
            allowClear={false}
            options={positionOptions}
            placeholder="Select position..."
          />
        </Col>
        <Col span={12}>
          <FormCheckbox
            disabled={!isFormEnabled}
            label="Exclude from mobile app search"
            name="isExcludedFromSearch"
          />
          <FormCheckbox
            disabled={!isFormEnabled}
            label="Exclude from mobile app browsing"
            name="isExcludedFromBrowsing"
          />
        </Col>
      </Row>
      <FormButtons
        permissions={[PERMISSIONS.DISH_TYPE.UPDATE_SIMPLE_DETAILS]}
        isDisabled={!isDirty}
        onReset={resetForm}
      />
    </DirtyPromptForm>
  )
}

DishTypeSimpleDetailsForm.propTypes = {
  id: idPropType.isRequired,
  description: PropTypes.string,
  synonyms: PropTypes.string,
  name: PropTypes.string.isRequired,
  position: PropTypes.number.isRequired,
  isExcludedFromSearch: PropTypes.bool.isRequired,
  isExcludedFromBrowsing: PropTypes.bool.isRequired,
  maxPosition: PropTypes.number.isRequired,
}

DishTypeSimpleDetailsForm.defaultProps = {
  description: undefined,
  synonyms: undefined,
}

export default DishTypeSimpleDetailsForm
